import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { IconRoute, routes } from '../../../../app.routes';
import { RouteUtils } from '../../../../utils/route.utils';
import { UnderConstructionComponent } from '../../../pages/under-construction/under-construction.component';
import { AppConfigService } from '../../../../services/app-config.service';
import { AssetContextService } from '../../../../services/asset-context.service';
import { AuthService } from '../../../../services/auth.service';
import { PermissionService } from '../../../../services/permission.service';
import { UserImpersonationService } from '../../../../services/user-impersonation.service';
import { UserStore } from '../../../../stores/user.store';
import { GenericSidebarComponent, SidebarSection } from '../generic-sidebar/generic-sidebar.component';

@Component({
  selector: 'app-configuration-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GenericSidebarComponent],
  host: { class: 'relative block h-full' },
  templateUrl: './configuration-sidebar.component.html',
})
export class ConfigurationSidebarComponent implements OnInit {
  protected readonly router = inject(Router);
  private readonly configService = inject(AppConfigService);
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService);
  private readonly impersonationService = inject(UserImpersonationService);
  private readonly assetContextService = inject(AssetContextService);
  private readonly userStore = inject(UserStore);
  private readonly destroyRef = inject(DestroyRef);
  readonly isCollapsed = signal<boolean>(this.configService.get<boolean>(GenericSidebarComponent.CONFIG_KEY, false));
  menuSections: SidebarSection[] = [];
  private readonly routePermissionsCache = signal<Map<string, boolean>>(new Map());
  private destroyed = false;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
    });
    this.extractRoutes();
    void this.preloadPermissions();
  }

  updateCollapsedState(value: boolean): void {
    this.isCollapsed.set(value);
  }

  private extractRoutes(): void {
    const configRoute = routes.find(route => route.path === 'configuration');
    if (!configRoute?.children) return;

    this.menuSections = RouteUtils.toSidebarSections(configRoute.children);
  }

  // Preload permissions to avoid async calls during rendering
  private async preloadPermissions(): Promise<void> {
    const realUserId = this.authService.currentUserId();
    const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);
    const assetId = this.assetContextService.getCurrentAssetId();

    if (!effectiveUserId || !assetId) {
      return;
    }

    // Resolve every route permission in parallel rather than serially awaiting each one.
    const entries = await Promise.all(
      this.menuSections.flatMap(section =>
        section.routes.map(async route => {
          const fullPath = `configuration/${route.path}`;
          try {
            const canAccess = await this.permissionService.canAccessRoute(effectiveUserId, assetId, fullPath);
            return [route.path ?? '', canAccess] as const;
          } catch (error) {
            console.error(`Error loading permission for ${fullPath}:`, error);
            return [route.path ?? '', false] as const;
          }
        }),
      ),
    );

    // Guard against a late resolution writing into a destroyed component.
    if (this.destroyed) return;
    this.routePermissionsCache.set(new Map(entries));
  }

  // Disable routes that are under construction or restricted by permissions
  isRouteDisabled = (route: IconRoute): boolean => {
    if (route.component === UnderConstructionComponent) {
      return true;
    }

    // Admins have access to everything
    const realUserId = this.authService.currentUserId();
    const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);

    // No resolved user → fail closed: hide the route rather than revealing it (was fail-open).
    if (!effectiveUserId) return true;

    const user = this.userStore.entities().find(u => u.id === effectiveUserId);
    if (user?.isAdmin) return false;

    // Check from preloaded cache
    const permissions = this.routePermissionsCache();
    const canAccess = permissions.get(route.path ?? '');

    // Cache miss (not yet resolved or denied) → fail-closed.
    if (canAccess === undefined) return true;

    return !canAccess;
  };

  navigateHome(): void {
    void this.router.navigate(['/']);
  }
}
